import { useEffect, useRef, useState } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { rows } from '../../lib/stock.js'
import { useTypeahead } from '../../lib/useTypeahead.js'

// Seller autocomplete over /api/sellers (debounced, min 2 chars). Xano matches
// the query against BOTH code and name, so every row shows its code — staff who
// know it can type it straight in.
//
// Keyboard: ↑/↓ move, Enter picks the highlighted row, Esc closes. Hover moves
// the highlight too, so mouse and keyboard never disagree about the target.
export default function SellerPicker({ onPick, width = 'w-[240px]', autoFocus = false }) {
  const [q, setQ] = useState('')
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  const needle = q.trim()
  const showList = open && needle.length >= 2
  // Only the real rows are indexable — the loading and empty states are not
  // selectable, so they must not shift the highlight's frame of reference.
  const count = showList && !loading ? list.length : 0

  const pick = (s) => { onPick?.(s); setQ(''); setOpen(false) }
  const ta = useTypeahead({
    count,
    onSelect: (i) => { const s = list[i]; if (s) pick(s) },
    onClose: () => setOpen(false),
  })

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  useEffect(() => {
    if (needle.length < 2) { setList([]); return }
    let alive = true
    setLoading(true)
    const t = setTimeout(() => {
      apiGet('/api/sellers', { search: needle, per_page: 20 })
        .then((r) => { if (alive) setList(rows(r)) })
        .catch(() => { if (alive) setList([]) })
        .finally(() => { if (alive) setLoading(false) })
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [needle])

  return (
    <div className="relative" ref={ref}>
      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input value={q} autoFocus={autoFocus}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={ta.onKeyDown}
        placeholder="ค้นหาด้วยรหัส หรือ ชื่อบริษัท…"
        className={`${width} rounded-lg border border-slate-200 bg-white pl-8 pr-2.5 py-1.5 text-[13px] text-slate-800 placeholder:text-slate-400 focus:border-violet-300 outline-none`} />
      {showList && (
        <div ref={ta.listRef}
          className="absolute z-30 mt-1 w-[380px] max-w-[92vw] min-w-full rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 max-h-[280px] overflow-y-auto py-1">
          {loading && <div className="px-3 py-2 text-[12.5px] text-slate-400"><Loader2 size={12} className="inline animate-spin mr-1.5" />กำลังค้นหา…</div>}
          {!loading && list.length === 0 && <div className="px-3 py-2 text-[12.5px] text-slate-400">ไม่พบผู้ขายที่ตรงกับ “{needle}”</div>}
          {!loading && list.map((s, i) => (
            <button key={s.id} type="button" data-ti-index={i}
              onMouseEnter={() => ta.setActiveIndex(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(s) }}
              className={`w-full text-left px-3 py-2 transition ${i === ta.activeIndex ? 'bg-violet-50' : 'hover:bg-violet-50'}`}>
              <div className="text-[13px] text-slate-800 leading-snug break-words">{s.name || '(ไม่มีชื่อ)'}</div>
              {(s.code || s.taxid) && (
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  {s.code && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">{s.code}</span>
                  )}
                  {s.taxid && <span className="text-[11px] text-slate-400 tabular-nums">{s.taxid}</span>}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
